import {Type} from '@github-ui/react-query-persister/typebox'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  ReviewerSchema as ProtoReviewerSchema,
  type Reviewer as ProtoReviewer,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'

// Formatted the following types using typebox to support the review requests API persister

const ReviewStateSchema = Type.Union([
  Type.Literal('pending'),
  Type.Literal('commented'),
  Type.Literal('approved'),
  Type.Literal('changes_requested'),
  Type.Literal('dismissed'),
])

const DeferredTypeSchema = Type.Union([Type.Literal('codeowner'), Type.Literal('copilot')])

const ReviewerBaseProperties = {
  id: Type.Number(),
  displayLogin: Type.String(),
  name: Type.Union([Type.String(), Type.Null()]),
  avatarUrl: Type.String(),
  // Populated only for bot reviewers (e.g. Copilot); null for users and teams. Used to source the
  // hovercard `botSlug` so it no longer depends on the soon-to-be-deprecated legacy display login.
  slug: Type.Union([Type.String(), Type.Null()]),
}

const UserReviewerSchema = Type.Object({
  ...ReviewerBaseProperties,
  reviewerType: Type.Literal('User'),
  organizationLogin: Type.Null(),
})

const TeamReviewerSchema = Type.Object({
  ...ReviewerBaseProperties,
  reviewerType: Type.Literal('Team'),
  organizationLogin: Type.Union([Type.String(), Type.Null()]),
})

export const ReviewerSchema = Type.Union([UserReviewerSchema, TeamReviewerSchema])

const PendingReviewRequestBaseProperties = {
  reviewer: ReviewerSchema,
  isCodeOwner: Type.Boolean(),
  isCodeOwnerFromDelegation: Type.Boolean(),
  delegatedFromTeam: Type.Union([TeamReviewerSchema, Type.Null()]),
}

const ActivePendingReviewRequestSchema = Type.Object({
  ...PendingReviewRequestBaseProperties,
  deferredType: Type.Null(),
  createdAt: Type.Union([Type.String(), Type.Null()]),
})

const DeferredPendingReviewRequestSchema = Type.Object({
  ...PendingReviewRequestBaseProperties,
  deferredType: DeferredTypeSchema,
  createdAt: Type.Union([Type.String(), Type.Null()]),
})

const PendingReviewRequestSchema = Type.Union([ActivePendingReviewRequestSchema, DeferredPendingReviewRequestSchema])

const CompletedReviewSchema = Type.Object({
  id: Type.Number(),
  canReRequest: Type.Boolean(),
  hasWriteAccess: Type.Boolean(),
  headSha: Type.String(),
  onBehalfOfTeams: Type.Array(TeamReviewerSchema),
  reviewer: UserReviewerSchema,
  state: ReviewStateSchema,
  submittedAt: Type.String(),
})

export const ReviewRequestsDataSchema = Type.Object({
  pendingReviewRequests: Type.Array(PendingReviewRequestSchema),
  completedReviews: Type.Array(CompletedReviewSchema),
})

// Legacy (flag-off) response types. The canonical shape consumers use is the
// generated proto type `ReviewRequestsResponse`
// (@github-ui/dotcom-schema/types/pull_requests/v1/review_requests); these
// Legacy* types describe the pre-proto JSON and are used only by the
// legacy->proto shim in the queryFn and by the Typebox persister/validators.
// They (and the shim) are deleted once the review_requests_proto flag is fully
// rolled out.
export type LegacyReviewState = Type.Static<typeof ReviewStateSchema>
export type LegacyDeferredType = Type.Static<typeof DeferredTypeSchema>
export type LegacyReviewerType =
  | Type.Static<typeof UserReviewerSchema>['reviewerType']
  | Type.Static<typeof TeamReviewerSchema>['reviewerType']
export type LegacyReviewer = Type.Static<typeof ReviewerSchema>
export type LegacyUserReviewer = Type.Static<typeof UserReviewerSchema>
export type LegacyTeamReviewer = Type.Static<typeof TeamReviewerSchema>
export type LegacyActivePendingReviewRequest = Type.Static<typeof ActivePendingReviewRequestSchema>
export type LegacyDeferredPendingReviewRequest = Type.Static<typeof DeferredPendingReviewRequestSchema>
export type LegacyPendingReviewRequest = Type.Static<typeof PendingReviewRequestSchema>
export type LegacyCompletedReview = Type.Static<typeof CompletedReviewSchema>
export type LegacyReviewRequestsData = Type.Static<typeof ReviewRequestsDataSchema>

// Temporary legacy->proto bridge for endpoints that are not yet on proto
// (review_summaries `onBehalfOfTeams`, available_reviewers) but feed shared,
// proto-typed reviewer components. Delete once those endpoints are migrated.
export function legacyReviewerToProto(reviewer: LegacyReviewer): ProtoReviewer {
  return create(ProtoReviewerSchema, {
    id: BigInt(reviewer.id),
    displayLogin: reviewer.displayLogin,
    name: reviewer.name ?? '',
    avatarUrl: reviewer.avatarUrl,
    reviewerType: reviewer.reviewerType,
    organizationLogin: reviewer.organizationLogin ?? '',
    slug: reviewer.slug ?? '',
  })
}
